import { useCallback, useEffect, useRef, useState } from 'react'

/** Пауза ввода, после которой правка уходит сама. */
export const LESSON_TEXT_AUTOSAVE_MS = 800

interface Saved {
  name: string
  description: string
}

/**
 * Черновик названия и описания урока (kkc82): раньше он уходил только по уходу из
 * поля, и перезагрузка до клика в сторону молча теряла текст. Теперь — сам после
 * паузы ввода, при смене урока (размонтирование) и при уходе со страницы; если
 * правка ещё не ушла, браузер спросит, точно ли уходить.
 */
export const useLessonTextAutosave = (
  name: string,
  description: string,
  onSave: (name: string, description: string) => void,
) => {
  const [t, setT] = useState(name)
  const [d, setD] = useState(description)
  // Последнее отправленное: ответ сервера на него — эхо нашей же правки, и оно не
  // должно затирать то, что автор успел допечатать, пока запрос летел.
  const sent = useRef<Saved>({ name, description })

  useEffect(() => {
    if (name !== sent.current.name) setT(name)
    sent.current.name = name
  }, [name])
  useEffect(() => {
    if (description !== sent.current.description) setD(description)
    sent.current.description = description
  }, [description])

  const latest = useRef({ t, d, onSave })
  latest.current = { t, d, onSave }

  const isDirty = useCallback(() => {
    const { t: tt, d: dd } = latest.current
    return tt !== sent.current.name || dd !== sent.current.description
  }, [])

  const commit = useCallback(() => {
    if (!isDirty()) return
    const { t: tt, d: dd, onSave: save } = latest.current
    sent.current = { name: tt, description: dd }
    save(tt, dd)
  }, [isDirty])

  useEffect(() => {
    const id = setTimeout(commit, LESSON_TEXT_AUTOSAVE_MS)
    return () => clearTimeout(id)
  }, [t, d, commit])

  // Смена урока или языка размонтирует панель (`key`) — недописанное уходит.
  useEffect(() => commit, [commit])

  useEffect(() => {
    const onUnload = (e: BeforeUnloadEvent) => {
      if (!isDirty()) return
      commit()
      e.preventDefault()
    }
    window.addEventListener('beforeunload', onUnload)
    return () => window.removeEventListener('beforeunload', onUnload)
  }, [commit, isDirty])

  return { t, setT, d, setD, commit }
}
